// ─── Microsoft Clarity Cross-Domain Tracking ─────────────────────────────────
// Bridges anonymous sessions on ebullion.in with authenticated
// customer sessions on secure.ebullion.in using shared cookies
//
// ID Strategy:
//   USER (completed KYC)     → identified by userId   (priority)
//   CUSTOMER (registered)    → identified by customerId (fallback)
//   App.tsx passes:  user?.userId || customerId
// ─────────────────────────────────────────────────────────────────────────────

declare global {
  interface Window {
    clarity: (...args: any[]) => void;
  }
}

const COOKIE_DOMAIN        = '.ebullion.in';
const CUSTOMER_ID_COOKIE   = 'eb_customer_id'; // read by ebullion.in to re-identify
const GUEST_ID_COOKIE      = 'eb_guest_id';    // written by ebullion.in for anon visitors

// ─── Cookie Helpers ───────────────────────────────────────────────────────────

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  return (
    document.cookie
      .split('; ')
      .find((r) => r.startsWith(`${name}=`))
      ?.split('=')[1] ?? null
  );
}

function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = [
    `${name}=${value}`,
    `domain=${COOKIE_DOMAIN}`,
    `path=/`,
    `expires=${expires.toUTCString()}`,
    `SameSite=Lax`,
    `Secure`,
  ].join('; ');
}

// ─── Clarity Script Loader ────────────────────────────────────────────────────

function loadClarityScript(projectId: string): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.clarity === 'function') {
      resolve();
      return;
    }

    (function (c: any, l: any, a: any, r: any, i: any) {
      c[a] =
        c[a] ||
        function () {
          (c[a].q = c[a].q || []).push(arguments);
        };
      const t: HTMLScriptElement = l.createElement(r);
      t.async = true;
      t.src = 'https://www.clarity.ms/tag/' + i;
      t.onload = () => resolve();
      const y = l.getElementsByTagName(r)[0];
      y.parentNode?.insertBefore(t, y);
    })(window, document, 'clarity', 'script', projectId);

    setTimeout(resolve, 3000); // fallback if onload doesn't fire
  });
}

// ─── Identity Init ────────────────────────────────────────────────────────────

function initClarityIdentity(trackingId: string): void {
  if (typeof window.clarity !== 'function') {
    console.warn('[Clarity] clarity() not available yet');
    return;
  }

  // 1. ✅ Master identity — search this ID in Clarity dashboard
  //    to see complete journey across secure + ebullion.in
  window.clarity('identify', trackingId, null, null, trackingId);

  // 2. Write to shared cookie so ebullion.in picks it up on next visit
  //    and calls identify(trackingId) there too → full cross-domain journey
  setCookie(CUSTOMER_ID_COOKIE, trackingId);

  // 3. Link pre-login anonymous session from ebullion.in
  //    Open session in Clarity → check tag pre_login_guest_id
  //    → search that value in Recordings → find anonymous journey
  const guestId = getCookie(GUEST_ID_COOKIE);
  if (guestId) {
    window.clarity('set', 'pre_login_guest_id', guestId);
    window.clarity('set', 'journey_source', 'ebullion.in');
  }

  // 4. Segmentation tags
  window.clarity('set', 'portal', 'secure');
  window.clarity('set', 'auth_status', 'authenticated');

  if (process.env.NODE_ENV === 'development') {
    console.log(
      '[Clarity] Identified →', trackingId,
      '| Pre-login guest:', guestId ?? 'none'
    );
  }
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export async function initClarity(trackingId: string | null | undefined): Promise<void> {
  const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;

  if (!projectId) {
    console.warn('[Clarity] REACT_APP_CLARITY_PROJECT_ID is not set');
    return;
  }

  if (!trackingId) {
    console.warn('[Clarity] No tracking ID available yet — skipping');
    return;
  }

  if (process.env.NODE_ENV !== 'production') return; // production only

  await loadClarityScript(projectId);

  setTimeout(() => {
    initClarityIdentity(trackingId);
  }, 1000);
}